/**
 * PythonCodeFormatter - Helper for formatting Python code
 *
 * This helper provides Python code formatting using Black formatter
 * and basic formatting utilities for code improvement.
 */

import { CdnModuleService } from '../../cdn-module/cdn-module.service';
import { STEPS_NAMESPACE } from '../../../constants/general.constants';
import { LazyPackageInstall } from './lazy-package-install.helper';
import { runPythonIsolated } from '../helpers/python-isolated.helper';

/** Pinned: newer Black releases have not worked in Pyodide. */
const BLACK_SPEC = 'black==22.12.0';

export interface FormattingResult {
  success: boolean;
  formattedCode?: string;
  error?: string;
  usedFormatter?: 'black' | 'basic' | 'none';
  changes?: number;
}

export interface FormattingConfig {
  useBlack?: boolean;
  lineLength?: number;
  enableLogging?: boolean;
  fallbackToBasic?: boolean;
  skipStringFormatting?: boolean;
  experimentalStringProcessing?: boolean;
  stringProcessing?: boolean;
}

export class PythonCodeFormatter {
  private readonly python: any;
  private readonly cdnModuleService?: CdnModuleService;
  private config: FormattingConfig;
  private readonly black: LazyPackageInstall;
  /**
   * `_transform_for_realtime`, defined in a namespace of its own so the
   * transformer's names (`ast`, its classes) never enter `__main__` (EHQ-117).
   */
  private realtimeTransform?: (code: string) => string;

  private get blackInstalled(): boolean {
    return this.black.isInstalled;
  }

  private set blackInstalled(installed: boolean) {
    this.black.setInstalled(installed);
  }

  constructor(
    python: any,
    micropip: any,
    cdnModuleService?: CdnModuleService,
    config: FormattingConfig = {}
  ) {
    this.python = python;
    this.black = new LazyPackageInstall(micropip, BLACK_SPEC, 'Python formatter');
    this.cdnModuleService = cdnModuleService;
    this.config = {
      useBlack: true,
      lineLength: 88,
      enableLogging: false,
      fallbackToBasic: true,
      skipStringFormatting: false,
      experimentalStringProcessing: true,
      stringProcessing: true,
      ...config
    };
  }

  /**
   * Initialize the formatter (install Black if needed) and real-time logging transformer
   */
  async initialize(): Promise<void> {
    if (this.config.useBlack && this.cdnModuleService) {
      await this.installBlack();
    }
    // Install AST transformer for real-time ehq.log() output
    this.installRealTimeLogTransformer();
  }

  /**
   * Format Python code using Black or basic formatting
   */
  async formatCode(content: string): Promise<FormattingResult> {
    if (!content || content.trim() === '') {
      return {
        success: true,
        formattedCode: content,
        usedFormatter: 'none',
        changes: 0
      };
    }

    // Try Black formatting first if enabled
    if (this.config.useBlack) {
      const blackResult = await this.formatWithBlack(content);
      if (blackResult.success) {
        return blackResult;
      } else if (this.config.fallbackToBasic) {
        return this.formatWithBasicRules(content);
      } else {
        return blackResult;
      }
    }

    // Use basic formatting
    return this.formatWithBasicRules(content);
  }

  /**
   * Format code using Black Python formatter
   */
  private async formatWithBlack(content: string): Promise<FormattingResult> {
    try {
      // Ensure Black is installed
      if (!this.blackInstalled) {
        const installResult = await this.installBlack();
        if (!installResult) {
          return {
            success: false,
            error: 'Failed to install Black formatter',
            usedFormatter: 'black'
          };
        }
      }

      // Runs in a scratch namespace, not __main__, so the driver's imports and
      // helpers never bind names step code could see or shadow (EHQ-117).
      const formattingCode = `
import sys, types, json

# (optional, if you're installing a newer version of Black and it crashes in Pyodide because of multiprocessing)
# import types, sys
# sys.modules["_multiprocessing"] = types.ModuleType("_multiprocessing")

from black import Mode, format_str
try:
    from black import TargetVersion
except ImportError:
    TargetVersion = None

def _pick_target_versions():
    if TargetVersion is None:
        return set()
    # Take the newest available member of the enumeration
    for name in ("PY313","PY312","PY311","PY310","PY39","PY38","PY37","PY36"):
        if hasattr(TargetVersion, name):
            return {getattr(TargetVersion, name)}
    return set()

def format_with_black(code, line_length=${this.config.lineLength}):
    try:
        mode = Mode(
            line_length=line_length,
            target_versions=_pick_target_versions(),
            string_normalization=${this.config.skipStringFormatting ? 'False' : 'True'},
            magic_trailing_comma=True,
            preview=True,
        )
        formatted = format_str(code, mode=mode)

        original_lines = code.split('\\n')
        formatted_lines = formatted.split('\\n')
        changes = sum(1 for a, b in zip(original_lines, formatted_lines) if a != b)
        changes += abs(len(original_lines) - len(formatted_lines))

        return {"success": True, "formatted_code": formatted, "changes": changes, "error": None}
    except Exception as e:
        return {"success": False, "formatted_code": None, "changes": 0, "error": str(e)}

code_to_format = ${JSON.stringify(content)}
result = format_with_black(code_to_format)
json.dumps(result)
`;

      const resultJson = runPythonIsolated(this.python, formattingCode);
      const result = JSON.parse(resultJson);

      return {
        success: result.success,
        formattedCode: result.formatted_code,
        error: result.error,
        usedFormatter: 'black',
        changes: result.changes
      };

    } catch (error: any) {
      return {
        success: false,
        error: `Black formatting error: ${error.message}`,
        usedFormatter: 'black'
      };
    }
  }

  /**
   * Basic formatting fallback — returns original code unchanged.
   * The previous implementation used simplistic indent tracking that
   * never properly decreased indent after raise/return/break, causing
   * flat code to become deeply nested and syntactically broken.
   * Without a real Python parser, basic reformatting does more harm
   * than good, so we preserve the original indentation.
   */
  private formatWithBasicRules(content: string): Promise<FormattingResult> {
    return Promise.resolve({
      success: true,
      formattedCode: content,
      usedFormatter: 'basic',
      changes: 0
    });
  }

  /**
   * Install Black formatter
   */
  private async installBlack(): Promise<boolean> {
    if (!this.cdnModuleService) {
      return false;
    }

    // Some packages are not working from CDN, so Black comes from micropip.
    return this.black.install();
  }

  /**
   * Format code with specific formatter
   */
  async formatWithSpecificFormatter(
    content: string,
    formatter: 'black' | 'basic'
  ): Promise<FormattingResult> {
    if (formatter === 'black') {
      return this.formatWithBlack(content);
    } else {
      return this.formatWithBasicRules(content);
    }
  }

  /**
   * Get formatting statistics
   */
  getFormattingStats(): {
    blackAvailable: boolean;
    blackInstalled: boolean;
    preferredFormatter: string;
  } {
    return {
      blackAvailable: this.blackInstalled,
      blackInstalled: this.blackInstalled,
      preferredFormatter: this.config.useBlack ? 'black' : 'basic'
    };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<FormattingConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): FormattingConfig {
    return { ...this.config };
  }

  /**
   * Reset Black installation status (for testing)
   */
  resetBlackInstallation(): void {
    this.blackInstalled = false;
  }

  /**
   * Basic code formatter — returns original code unchanged.
   * See formatWithBasicRules() for why the naive implementation was removed.
   */
  basicFormatCode(code: string): string {
    return code;
  }

  // ================= REAL-TIME LOGGING METHODS =================

  /**
   * Install AST transformer for real-time ehq.log() output.
   * Injects Python code that transforms ehq.log() → await ehq.log()
   * for top-level calls and inside async functions.
   *
   * Does NOT transform ehq.log() inside regular functions or lambdas
   * (would cause SyntaxError: 'await' outside async function).
   */
  private installRealTimeLogTransformer(): void {
    if (this.realtimeTransform) {
      return;
    }

    const transformerCode = `
import ast

class _RealTimeLogTransformer(ast.NodeTransformer):
    """Transform ehq.log() calls to await ehq.log() for real-time UI updates.

    Only transforms:
    - Top-level ehq.log() calls (wrapped in async context)
    - ehq.log() inside async functions

    Does NOT transform (would cause SyntaxError):
    - ehq.log() inside regular def functions
    - ehq.log() inside lambdas
    """

    def __init__(self):
        self.has_transforms = False
        self.in_sync_context = False  # True when inside regular def or lambda

    def visit_AsyncFunctionDef(self, node):
        """Async function - await is allowed"""
        old_value = self.in_sync_context
        self.in_sync_context = False
        self.generic_visit(node)
        self.in_sync_context = old_value
        return node

    def visit_FunctionDef(self, node):
        """Regular function - await NOT allowed"""
        old_value = self.in_sync_context
        self.in_sync_context = True
        self.generic_visit(node)
        self.in_sync_context = old_value
        return node

    def visit_Lambda(self, node):
        """Lambda - await NOT allowed"""
        old_value = self.in_sync_context
        self.in_sync_context = True
        self.generic_visit(node)
        self.in_sync_context = old_value
        return node

    def visit_Expr(self, node):
        """Transform ehq.log() to await ehq.log() if not in sync context"""
        if self._is_ehq_log_call(node.value):
            if not self.in_sync_context:
                self.has_transforms = True
                new_node = ast.Expr(value=ast.Await(value=node.value))
                return ast.fix_missing_locations(new_node)
        return self.generic_visit(node)

    def _is_ehq_log_call(self, node):
        if not isinstance(node, ast.Call):
            return False
        if not isinstance(node.func, ast.Attribute):
            return False
        if node.func.attr != 'log':
            return False
        if not isinstance(node.func.value, ast.Name):
            return False
        return node.func.value.id.lower() == 'ehq'


def _transform_for_realtime(code: str) -> str:
    """Transform Python code for real-time ehq.log() output"""
    try:
        tree = ast.parse(code)
        transformer = _RealTimeLogTransformer()
        new_tree = transformer.visit(tree)

        if not transformer.has_transforms:
            return code

        has_async = any(
            isinstance(node, (ast.AsyncFunctionDef, ast.Await))
            for node in ast.walk(tree)
        )

        if has_async:
            return ast.unparse(new_tree)

        # Wrap in async function with top-level await (Pyodide supports this)
        indented = '\\n'.join('    ' + line for line in ast.unparse(new_tree).split('\\n'))
        return f'''async def __realtime_main__():
{indented}

await __realtime_main__()
'''
    except SyntaxError:
        return code
`;

    // Defined in its own namespace: step code runs in __main__, where these
    // names would leak into user code (a bare `ast.parse` would work here but
    // not on enoch) and reassigning `ast` would break every later transform.
    const namespace = this.python.toPy({});
    try {
      this.python.runPython(transformerCode, { globals: namespace });
      this.realtimeTransform = namespace.get('_transform_for_realtime');
    } catch (error) {
      console.warn('PythonCodeFormatter: real-time log transformer installation failed', error);
    } finally {
      // The function keeps its own reference to the namespace it closes over.
      namespace.destroy?.();
    }
  }

  /**
   * Transform code for real-time ehq.log() output.
   * Uses Pyodide globals to pass code safely without escaping issues.
   *
   * @param code - Python code to transform
   * @returns Transformed code with await added to ehq.log() calls
   */
  transformForRealTimeLogging(code: string): string {
    if (!this.python || !code) {
      return code;
    }

    this.installRealTimeLogTransformer();
    if (!this.realtimeTransform) {
      return code;
    }

    try {
      // Pass the source as an argument; nothing is bound in __main__.
      return this.realtimeTransform(code);
    } catch (error) {
      console.warn('PythonCodeFormatter: real-time log transformation failed, using original code', error);
      return code;
    }
  }

  /**
   * Get Python logger bootstrap code.
   * Sets up logging.Handler that routes Python logging to ehq.log().
   *
   * @returns Python code string for logger setup
   */
  static getLoggerSetupCode(): string {
    return `
import logging
import ehq

_EHQ_LOG_LEVELS = {
    'CRITICAL': 'error',
    'ERROR': 'error',
    'WARNING': 'warn',
    'WARN': 'warn',
    'INFO': 'info',
    'DEBUG': 'log',
    'NOTSET': 'log',
}

_ehq_allowed_log_prefixes = {'${STEPS_NAMESPACE}'}

class _EHQLogHandler(logging.Handler):
    def emit(self, record):
        if '.' in record.name:
            if not any(record.name == p or record.name.startswith(p + '.')
                       for p in _ehq_allowed_log_prefixes):
                return

        try:
            message = self.format(record)
        except Exception:
            try:
                message = record.getMessage()
            except Exception:
                message = str(record)

        level_name = getattr(record, 'levelname', 'INFO')
        level = _EHQ_LOG_LEVELS.get(level_name, 'log')

        try:
            ehq.log(message, level)
        except Exception:
            pass

def _ensure_ehq_log_handler():
    root_logger = logging.getLogger()
    ehq_handler = None

    for handler in root_logger.handlers:
        if handler.__class__.__name__ == '_EHQLogHandler':
            ehq_handler = handler
            break

    if ehq_handler is None:
        ehq_handler = _EHQLogHandler()
        ehq_handler.setFormatter(logging.Formatter('%(message)s'))
        root_logger.addHandler(ehq_handler)

    if root_logger.level > logging.INFO:
        root_logger.setLevel(logging.INFO)

_ensure_ehq_log_handler()
`;
  }

  /**
   * Inject logger setup code before user code.
   *
   * @param content - User's Python code
   * @returns Code with logger setup prepended
   */
  static injectLoggerSetup(content: string): string {
    const bootstrap = PythonCodeFormatter.getLoggerSetupCode().trim();
    return `${bootstrap}\n\n${content}`;
  }
}
